import { Tabs as ArkTabs } from "@ark-ui/react";
import {
  AromaTabsVariantProps,
  aromaTabs,
} from "@zenchef/styled-system/recipes";
import React, { ElementRef, forwardRef } from "react";

type TabsContentProps = AromaTabsVariantProps &
  Omit<ArkTabs.ContentProps, "value"> & {
    tabKey: string;
  };

const TabContent = forwardRef<ElementRef<"div">, TabsContentProps>(
  ({ children, tabKey, ...propsWithVariants }, ref) => {
    const [variants, props] = aromaTabs.splitVariantProps(propsWithVariants);
    const classes = aromaTabs(variants);

    return (
      <ArkTabs.Content
        ref={ref}
        key={`${tabKey}_body`}
        className={classes.content}
        {...props}
        value={tabKey}
      >
        {children}
      </ArkTabs.Content>
    );
  },
);

interface TabsItemProps {
  key: string;
  title: string;
}

export type TabsProps = AromaTabsVariantProps &
  ArkTabs.RootProps &
  React.HTMLAttributes<HTMLDivElement> & {
    testId?: string;
    items: TabsItemProps[];
    defaultValue: string;
    children: React.ReactNode;
  };

/**
 * CoreTabs
 * @returns Styled number input implemented with arkui.
 */
const Tabs = forwardRef<ElementRef<"div">, TabsProps>(
  ({ testId, items, defaultValue, children, ...propsWithVariants }, ref) => {
    const [variants, props] = aromaTabs.splitVariantProps(propsWithVariants);
    const classes = aromaTabs(variants);

    return (
      <ArkTabs.Root
        className={classes.root}
        ref={ref}
        data-testid={testId}
        defaultValue={defaultValue}
        {...props}
      >
        <ArkTabs.List className={classes.list}>
          {items.map((item, index) => (
            <ArkTabs.Trigger
              key={`${item.key}_${index}_tab`}
              data-testid={`${item.key}_${testId}_trigger`}
              value={item.key}
              className={classes.trigger}
            >
              {item.title}
            </ArkTabs.Trigger>
          ))}
        </ArkTabs.List>
        {children}
      </ArkTabs.Root>
    );
  },
);

interface TabsComponent
  extends React.ForwardRefExoticComponent<
    TabsProps & React.RefAttributes<HTMLElement>
  > {
  Content: typeof TabContent;
}

const TabsWithContent = Object.assign(Tabs, { Content: TabContent });

export default TabsWithContent as TabsComponent;
